import { z } from 'zod';
/**
 * Enterprise single sign-on (docs/adr/0055-sso-and-scim.md; brief: Identity → Enterprise SSO and provisioning).
 * One identity provider per workspace, over OIDC or SAML 2.0, on the Business and Enterprise plans (or a
 * complimentary workspace). People whose email is on a domain the workspace has verified sign in through it.
 */
export const SsoProtocol = z.enum(['oidc', 'saml']);
export const SsoPreset = z.enum(['google', 'microsoft', 'okta', 'generic']);
export const SSO_PRESETS = {
    google: { label: 'Google Workspace', issuerHint: 'https://accounts.google.com', issuerTemplate: 'https://accounts.google.com' },
    microsoft: { label: 'Microsoft Entra ID', issuerHint: 'https://login.microsoftonline.com/<directory (tenant) id>/v2.0', issuerTemplate: 'https://login.microsoftonline.com/{tenant}/v2.0' },
    okta: { label: 'Okta', issuerHint: 'https://<your-org>.okta.com', issuerTemplate: null },
    generic: { label: 'Another identity provider', issuerHint: 'https://idp.example.com', issuerTemplate: null },
};
/** A domain name as people type it: lower case, no scheme, no path, no trailing dot. */
export const DomainName = z
    .string()
    .trim()
    .toLowerCase()
    .transform((v) => v.replace(/\.$/, ''))
    .pipe(z.string().max(253).regex(/^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'Enter a domain like example.com'));
/** The TXT record that proves a domain: its name and the value to publish. */
export const SSO_TXT_PREFIX = '_chapsi-verification';
export const ssoTxtName = (domain) => `${SSO_TXT_PREFIX}.${domain}`;
export const ssoTxtValue = (token) => `chapsi-domain-verification=${token}`;
export const SsoDomain = z.object({
    id: z.uuid(),
    domain: z.string(),
    verified: z.boolean(),
    verifiedAt: z.string().nullable(),
    lastCheckedAt: z.string().nullable(),
    /**
     * Periodic re-checks of a verified domain (docs/adr/0055): when the TXT record was first found missing in the current
     * run of failed checks, when the owners were warned, and when the domain stops being verified unless it comes back.
     * All null while the record is in place.
     */
    recheck: z.object({ failingSince: z.string().nullable(), warnedAt: z.string().nullable(), lapsesAt: z.string().nullable() }),
    /** When the re-checks last marked this domain unverified because its record stayed missing (null otherwise). */
    lapsedAt: z.string().nullable(),
    /** The TXT record to add at the domain's DNS host. */
    txtName: z.string(),
    txtValue: z.string(),
});
export const SsoCertificate = z.object({
    subject: z.string(),
    /** ISO time the certificate stops being valid. */
    notAfter: z.string(),
    /** SHA-256 fingerprint, colon-separated hex. */
    fingerprint: z.string(),
});
export const SsoConnection = z.object({
    id: z.uuid(),
    protocol: SsoProtocol,
    preset: SsoPreset,
    oidc: z.object({ issuer: z.string(), clientId: z.string(), hasClientSecret: z.boolean() }).nullable(),
    saml: z.object({ idpEntityId: z.string(), ssoUrl: z.string(), certificates: z.array(SsoCertificate), metadataUrl: z.string().nullable() }).nullable(),
    updatedAt: z.string(),
});
export const SsoPolicy = z.object({
    /** Members on a verified domain can only sign in through the identity provider (owners keep a passkey break-glass). */
    requireSso: z.boolean(),
    /** First SSO sign-in of someone on a verified domain who is not a member yet makes them a member. */
    jitProvisioning: z.boolean(),
    /** The identity provider enforces MFA, so an SSO sign-in counts as two-factor sign-in here. Default off. */
    idpEnforcesMfa: z.boolean(),
    /** SAML only: accept sign-ins started from the identity provider's dashboard. Default off. */
    allowIdpInitiated: z.boolean(),
});
export const SsoSettings = z.object({
    /** The workspace's plan includes SSO (Business, Enterprise, or complimentary). */
    entitled: z.boolean(),
    connection: SsoConnection.nullable(),
    domains: z.array(SsoDomain),
    policy: SsoPolicy,
    /** What the owner copies into the identity provider: known before the connection is saved. */
    serviceProvider: z.object({
        oidcRedirectUri: z.string(),
        samlEntityId: z.string(),
        samlAcsUrl: z.string(),
        samlMetadataUrl: z.string(),
    }),
    /** Break-glass (docs/adr/0055): whether the caller and how many owners can sign in with a passkey. */
    breakGlass: z.object({ youHavePasskey: z.boolean(), ownersWithPasskey: z.number().int() }),
    /** Field-level encryption is set up on this server, so an OIDC client secret can be stored. */
    secretsAvailable: z.boolean(),
    /**
     * Step-up (docs/adr/0055): every change here needs a fresh second factor. `until` is when this device session's
     * confirmation runs out (five minutes after confirming), or null when the owner has to confirm first.
     */
    stepUp: z.object({ until: z.string().nullable() }),
    scim: z.object({
        enabled: z.boolean(),
        baseUrl: z.string(),
        tokenCreatedAt: z.string().nullable(),
        lastUsedAt: z.string().nullable(),
        managedMembers: z.number().int(),
    }),
});
const httpsUrl = z.url().max(1000).refine((u) => /^https?:\/\//i.test(u), 'Use an https:// address');
export const SaveSsoConnectionInput = z.discriminatedUnion('protocol', [
    z.object({
        protocol: z.literal('oidc'),
        preset: SsoPreset,
        issuer: httpsUrl,
        clientId: z.string().trim().min(1).max(500),
        /** Omit to keep the stored secret. */
        clientSecret: z.string().min(1).max(1000).optional(),
    }),
    z.object({
        protocol: z.literal('saml'),
        preset: SsoPreset,
        /** One of: the IdP's metadata URL, its metadata XML, or the three values typed in. */
        metadataUrl: httpsUrl.optional(),
        metadataXml: z.string().min(50).max(200_000).optional(),
        idpEntityId: z.string().trim().min(1).max(500).optional(),
        ssoUrl: httpsUrl.optional(),
        certificate: z.string().min(50).max(20_000).optional(),
    }),
]);
export const AddSsoDomainInput = z.object({ domain: DomainName });
export const UpdateSsoPolicyInput = SsoPolicy.partial();
/** How long one confirmation lets an owner change single sign-on from the device session that confirmed. */
export const SSO_STEP_UP_MINUTES = 5;
/** A step-up code (purpose `sso`) from the emailed code, passkey or authenticator step-up routes. */
export const ConfirmSsoStepUpInput = z.object({ code: z.string().regex(/^\d{6}$/, 'Enter the six-digit code') });
export const ScimTokenResponse = z.object({
    /** Shown once. Only a hash is stored. */
    token: z.string(),
    settings: SsoSettings,
});
// --- Signing in -----------------------------------------------------------------------------------------
export const SsoStartInput = z.object({ email: z.email().max(254) });
/** `sso: false`: the domain has no single sign-on; use the email link or a passkey. */
export const SsoStartResponse = z.object({ sso: z.boolean(), url: z.string().optional(), required: z.boolean().optional() });
/** The OIDC callback's query string, as the identity provider sends it. */
export const SsoOidcCallbackQuery = z.object({
    code: z.string().max(4000).optional(),
    state: z.string().max(500).optional(),
    iss: z.string().max(1000).optional(),
    session_state: z.string().max(1000).optional(),
    error: z.string().max(200).optional(),
    error_description: z.string().max(2000).optional(),
});
/** A SAML POST binding body (application/x-www-form-urlencoded). */
export const SsoSamlAcsBody = z.object({
    SAMLResponse: z.string().min(20).max(500_000),
    RelayState: z.string().max(500).optional(),
});
// --- Signing in on the phone app (docs/adr/0055, "Continue with SSO") ------------------------------------------
/**
 * Where a phone sign-in comes back to: the app's own scheme. A constant, never taken from a request. The address
 * carries only a single-use handoff code (two minutes), which is useless without the secret the app got from
 * `startMobileSso` over TLS and kept in the keychain (the same idea as PKCE for native apps, RFC 8252).
 */
export const MOBILE_SSO_REDIRECT = 'chapsi://sso-callback';
/** `sso: false`: no single sign-on for the domain; send the emailed code as usual. */
export const MobileSsoStartResponse = z.object({
    sso: z.boolean(),
    /** Open in the system browser: our launch address, which binds that browser and goes on to the identity provider. */
    url: z.string().optional(),
    required: z.boolean().optional(),
    /** Keep in the keychain; send with the handoff code to finish. Shown once, only its hash is stored. */
    secret: z.string().optional(),
});
export const MobileSsoExchangeInput = z.object({
    code: z.string().min(20).max(200),
    secret: z.string().min(20).max(200),
});
/** Callbacks answer 303 See Other to the web app; there is no JSON body. */
export const SsoRedirect = z.object({});
/** Why an SSO sign-in failed, as the web app shows it (`/sign-in?sso_error=…`). Never the IdP's own text. */
export const SsoError = z.enum(['failed', 'expired', 'not_member', 'suspended', 'domain', 'unavailable', 'denied']);
export const SSO_ERROR_MESSAGES = {
    failed: 'Single sign-on did not work. Try again, or ask your administrator.',
    expired: 'That sign-in took too long or was already used. Start again.',
    not_member: "Your identity provider signed you in, but you don't have access to this workspace yet. Ask an admin to invite you.",
    suspended: 'Your access to this workspace has been turned off by your administrator.',
    domain: 'Your identity provider sent an email address this workspace has not verified.',
    unavailable: 'Single sign-on is not available for this workspace right now.',
    denied: 'Sign-in was cancelled at your identity provider.',
};
//# sourceMappingURL=sso.js.map